import { useState, useEffect } from "react";
import axios from "axios";
import Header from "../Components/Header";
import SimpleTable from "../Components/SimpleTable";
import { BRAND } from "../css/brand";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import BlueButton from "../Components/BlueButton";
import { RefreshCw, Download } from "lucide-react";
import Loader from "../Components/Loader";
import GoBackButton from "../Components/GoBackButton";
import TableSkeleton from "../Components/TableSkeleton";

export default function ActivationReport() {
  const [report, setReport] = useState({ rows: [], keys: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const rowsPerPage = 10;

  // Date range states
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Auth data
  const authUserString = sessionStorage.getItem("authUserData1");
  const authUser = JSON.parse(authUserString);
  const companyID = authUser?.data?.[0]?.company_id;

  // Current months till now (Jan → current)
  const currentMonthIndex = new Date().getMonth();
  const allMonths = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const monthsTillNow = allMonths.slice(0, currentMonthIndex + 1);

  // 🔹 Fetch Report
  const fetchReport = async () => {
    setLoading(true);
    setError(null);

    try {
      let res;
      let data = {};

      if (fromDate && toDate) {
        const url = `https://spillas.com/api/v1/get-datewise-activations?from_date=${fromDate}&to_date=${toDate}&company_id=${companyID}`;
        res = await axios.get(url);
        const activations = res.data?.datewise_activations || res.data?.data || [];

        activations.forEach((item) => {
          const date = new Date(item.added_date);
          const monthName = allMonths[date.getMonth()];
          if (!data[monthName]) data[monthName] = [];
          data[monthName].push(item);
        });
      } else {
        const url = `https://spillas.com/api/v1/get-yearewise-activations?year=2025&company_id=${companyID}`;
        res = await axios.get(url);
        data = res.data?.monthly_activations || {};
      }

      const retailerMap = new Map();

      Object.entries(data).forEach(([period, entries]) => {
        entries.forEach((item) => {
          const key = item.org_id;
          if (!retailerMap.has(key)) {
            retailerMap.set(key, {
              org_id: item.org_id,
              retailer_name: item.retailer_name || "-",
              cnf_name: item.cnf_name === "null" ? "-" : item.cnf_name,
              ss_name: item.ss_name === "null" ? "-" : item.ss_name,
              dist_name: item.dist_name || "-",
              state: item.state || "-",
              district: item.district || "-",
              totals: {},
            });
          }

          retailerMap.get(key).totals[period] =
            (retailerMap.get(key).totals[period] || 0) + Number(item.total || 0);
        });
      });

      const allKeys = Object.keys(data);
      let filteredKeys = [];

      if (fromDate && toDate) {
        const start = new Date(fromDate);
        const end = new Date(toDate);
        let current = new Date(start);
        while (current <= end) {
          const monthName = allMonths[current.getMonth()];
          if (allKeys.includes(monthName) && !filteredKeys.includes(monthName)) {
            filteredKeys.push(monthName);
          }
          current.setMonth(current.getMonth() + 1);
        }
      } else {
        filteredKeys = monthsTillNow.filter((m) => allKeys.includes(m));
      }

      const filteredRetailers = Array.from(retailerMap.values()).filter((ret) => {
        if (!search.trim()) return true;
        const s = search.toLowerCase();
        return (
          `rtl${ret.org_id}`.toLowerCase().includes(s) ||
          ret.retailer_name.toLowerCase().includes(s)
        );
      });

      const formattedRows = filteredRetailers.map((ret, i) => {
        const totals = filteredKeys.map((k) => ret.totals[k] || 0);
        const allZero = totals.every((v) => v === 0);
        const hasAnyValue = totals.some((v) => v > 0);

        const monthCells = totals.map((val, idx) => {
          let cellClass = "text-center font-medium";
          if (allZero) cellClass += " text-red-600";
          else if (hasAnyValue && val === 0) cellClass += " text-orange-500";
          else cellClass += " text-gray-800";
          return (
            <span key={idx} className={cellClass}>
              {val}
            </span>
          );
        });

        return [
          i + 1,
          <span
            className={`font-semibold ${
              allZero ? "text-red-600" : "text-blue-800"
            }`}
          >
            {`RTL${ret.org_id} - ${ret.retailer_name}`}
          </span>,
          ret.cnf_name,
          ret.ss_name,
          ret.dist_name,
          ret.state,
          ret.district,
          ...monthCells,
        ];
      });

      setReport({ rows: formattedRows, keys: filteredKeys });
    } catch (err) {
      console.error("Error fetching report:", err);
      setError("Failed to load activation report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyID) fetchReport();
  }, [companyID, fromDate, toDate, search]);

  const totalPages = Math.ceil((report.rows || []).length / rowsPerPage);
  const paginate = (arr, p) => arr.slice((p - 1) * rowsPerPage, p * rowsPerPage);

  // ✅ CSV Download Function
  const handleDownload = () => {
  try {
    if (!report.rows || report.rows.length === 0) {
      alert("No data to download");
      return;
    }

    const headers = [
      "Sr No",
      "Retailer",
      "CNF",
      "Super Stockist",
      "Distributor",
      "State",
      "District",
      ...(fromDate && toDate ? report.keys : monthsTillNow),
    ];

    const csvRows = [headers.join(",")];

    report.rows.forEach((row) => {
      const rowData = row.map((cell) => {
        // 🧩 Handle JSX (React elements) or plain strings/numbers
        if (typeof cell === "string" || typeof cell === "number") {
          return `"${String(cell).replace(/"/g, '""')}"`;
        }

        if (cell?.props?.children) {
          // Flatten nested children (array, string, element, etc.)
          const extractText = (children) => {
            if (Array.isArray(children)) {
              return children.map(extractText).join("");
            } else if (typeof children === "object" && children?.props) {
              return extractText(children.props.children);
            } else {
              return String(children ?? "");
            }
          };
          return `"${extractText(cell.props.children).replace(/"/g, '""')}"`;
        }

        return '""';
      });

      csvRows.push(rowData.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `activation_report_${fromDate || "year"}_${toDate || ""}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("❌ Download error:", err);
    alert("Failed to download CSV");
  }
};


  return (
    <div className="space-y-4">
      <GoBackButton />
      <Header brand={BRAND} title="Activation Report" />

      {/* 🔹 Filters */}
      <div className="flex flex-wrap justify-end gap-3 items-end">
        {/* Search */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by code or name"
          className="border rounded px-2 py-1 w-60 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        {/* Date Range */}
        <div className="flex items-center gap-2 justify-center">
          <label className="block text-sm font-medium mb-1">Date Range</label>
          <div className="border rounded px-2 py-1 focus-within:ring-2 focus-within:ring-blue-400">
            <DatePicker
              selectsRange
              startDate={fromDate ? new Date(fromDate) : null}
              endDate={toDate ? new Date(toDate) : null}
              onChange={(dates) => {
                const [start, end] = dates;
                setFromDate(start ? start.toISOString().split("T")[0] : "");
                setToDate(end ? end.toISOString().split("T")[0] : "");
              }}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select date range"
              className="w-full outline-none text-sm text-gray-700"
            />
          </div>
        </div>

        {/* Buttons */}
        <BlueButton
          brand={BRAND}
          onClick={() => {
            setFromDate("");
            setToDate("");
            setSearch("");
          }}
        >
          Reset
        </BlueButton>

        <BlueButton brand={BRAND} onClick={fetchReport} className="inline-flex items-center gap-1">
          <RefreshCw size={14} /> Refresh
        </BlueButton>

        {/* ✅ Download CSV Button */}
        <BlueButton brand={BRAND} onClick={handleDownload} className="inline-flex items-center gap-1">
          <Download size={14} /> Download
        </BlueButton>
      </div>

      {/* 🔹 Table */}
      {loading && <TableSkeleton rows={10} cols={7} />}
      {error && <p className="text-red-500">{error}</p>}

      {!loading && !error && report.rows?.length > 0 && (
        <>
          <SimpleTable
            headers={[
              "Sr No",
              "Retailer",
              "CNF",
              "Super Stockist",
              "Distributor",
              "State",
              "District",
              ...(fromDate && toDate ? report.keys : monthsTillNow),
            ]}
            rows={paginate(report.rows, currentPage)}
          />
          {/* Pagination */}
          <div className="flex gap-2 justify-end mt-4 items-center">
            {/* Prev */}
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className={`px-2 py-1 border rounded text-sm ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-slate-100"
              }`}
            >
              Prev
            </button>

            {/* Page numbers */}
            {(() => {
              const pages = [];
              const maxVisible = 5;
              let start = Math.max(1, currentPage - 2);
              let end = Math.min(totalPages, start + maxVisible - 1);
              if (end - start < maxVisible - 1)
                start = Math.max(1, end - maxVisible + 1);
              if (start > 1) {
                pages.push(1);
                if (start > 2) pages.push("...");
              }
              for (let i = start; i <= end; i++) pages.push(i);
              if (end < totalPages) {
                if (end < totalPages - 1) pages.push("...");
                pages.push(totalPages);
              }
              return pages.map((p, idx) =>
                p === "..." ? (
                  <span key={idx} className="px-2 text-gray-500">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-2 py-1 border rounded text-sm ${
                      currentPage === p
                        ? "bg-blue-600 text-white"
                        : "bg-white hover:bg-slate-100"
                    }`}
                  >
                    {p}
                  </button>
                )
              );
            })()}

            {/* Next */}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className={`px-2 py-1 border rounded text-sm ${
                currentPage === totalPages
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-slate-100"
              }`}
            >
              Next
            </button>
          </div>
        </>
      )}

      {!loading && !error && report.rows?.length === 0 && <p>No data found.</p>}
    </div>
  );
}



// import { useState, useEffect } from "react";
// import axios from "axios";
// import Header from "../Components/Header";
// import SimpleTable from "../Components/SimpleTable";
// import { BRAND } from "../css/brand";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import BlueButton from "../Components/BlueButton";
// import { RefreshCw } from "lucide-react";
// import Loader from "../Components/Loader";
// import GoBackButton from "../Components/GoBackButton";
// import TableSkeleton from "../Components/TableSkeleton";

// export default function ActivationReport() {
//   const [report, setReport] = useState({ rows: [], keys: [] });
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);

//   const [page, setPage] = useState(1);
//   const [search, setSearch] = useState("");
//   const rowsPerPage = 10;

//   // Date range states
//   const [fromDate, setFromDate] = useState("");
//   const [toDate, setToDate] = useState("");

//   // Auth data
//   const authUserString = sessionStorage.getItem("authUserData1");
//   const authUser = JSON.parse(authUserString);
//   const companyID = authUser?.data?.[0]?.company_id;

//   // Current months till now (Jan → current)
//   const currentMonthIndex = new Date().getMonth();
//   const allMonths = [
//     "Jan", "Feb", "Mar", "Apr", "May", "Jun",
//     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
//   ];
//   const monthsTillNow = allMonths.slice(0, currentMonthIndex + 1);

//   // 🔹 Fetch Report
//   const fetchReport = async () => {
//     setLoading(true);
//     setError(null);

//     try {
//       let res;
//       let data = {};

//       // 🔹 Fetch from API
//       if (fromDate && toDate) {
//         const url = `https://spillas.com/api/v1/get-datewise-activations?from_date=${fromDate}&to_date=${toDate}&company_id=${companyID}`;
//         res = await axios.get(url);

//         const activations = res.data?.datewise_activations || res.data?.data || [];

//         // 🗓️ Group by month (e.g., "Feb", "Mar")
//         activations.forEach((item) => {
//           const date = new Date(item.added_date);
//           const monthName = allMonths[date.getMonth()]; // e.g. "Mar"

//           if (!data[monthName]) data[monthName] = [];
//           data[monthName].push(item);
//         });
//       } else {
//         // ✅ Yearwise API (Jan–current month)
//         const url = `https://spillas.com/api/v1/get-yearewise-activations?year=2025&company_id=${companyID}`;
//         res = await axios.get(url);
//         data = res.data?.monthly_activations || {};
//       }

//       // 🔹 Map retailer data
//       const retailerMap = new Map();

//       Object.entries(data).forEach(([period, entries]) => {
//         entries.forEach((item) => {
//           const key = item.org_id;
//           if (!retailerMap.has(key)) {
//             retailerMap.set(key, {
//               org_id: item.org_id,
//               retailer_name: item.retailer_name || "-",
//               cnf_name: item.cnf_name === "null" ? "-" : item.cnf_name,
//               ss_name: item.ss_name === "null" ? "-" : item.ss_name,
//               dist_name: item.dist_name || "-",
//               state: item.state || "-",
//               district: item.district || "-",
//               totals: {},
//             });
//           }

//           retailerMap.get(key).totals[period] =
//             (retailerMap.get(key).totals[period] || 0) + Number(item.total || 0);

//         });
//       });

//       // 🔹 Determine which month keys to display
//       const allKeys = Object.keys(data);
//       let filteredKeys = [];

//       if (fromDate && toDate) {
//         const start = new Date(fromDate);
//         const end = new Date(toDate);

//         // Include all months between fromDate → toDate
//         let current = new Date(start);
//         while (current <= end) {
//           const monthName = allMonths[current.getMonth()];
//           if (allKeys.includes(monthName) && !filteredKeys.includes(monthName)) {
//             filteredKeys.push(monthName);
//           }
//           current.setMonth(current.getMonth() + 1);
//         }
//       } else {
//         filteredKeys = monthsTillNow.filter((m) => allKeys.includes(m));
//       }

//       // 🔍 Search filter
//       const filteredRetailers = Array.from(retailerMap.values()).filter((ret) => {
//         if (!search.trim()) return true;
//         const s = search.toLowerCase();
//         return (
//           `rtl${ret.org_id}`.toLowerCase().includes(s) ||
//           ret.retailer_name.toLowerCase().includes(s)
//         );
//       });

//       // 🔹 Format rows
//       const formattedRows = filteredRetailers.map((ret, i) => {
//       const totals = filteredKeys.map((k) => ret.totals[k] || 0);
//       const allZero = totals.every((v) => v === 0);
//       const hasAnyValue = totals.some((v) => v > 0);

//       // 🧩 Build month cells with color badges
//       const monthCells = totals.map((val, idx) => {
//         let cellClass = "text-center font-medium";

//         if (allZero) {
//           cellClass += " text-red-600"; // all months red if all zero
//         } else if (hasAnyValue && val === 0) {
//           cellClass += " text-orange-500"; // orange for zero months if at least one > 0
//         } else {
//           cellClass += " text-gray-800"; // normal text for non-zero
//         }

//         return (
//           <span key={idx} className={cellClass}>
//             {val}
//           </span>
//         );
//       });

//       return [
//         i + 1,
//         <span
//           className={`font-semibold ${
//             allZero ? "text-red-600" : "text-blue-800"
//           }`}
//         >
//           {`RTL${ret.org_id} - ${ret.retailer_name}`}
//         </span>,
//         ret.cnf_name,
//         ret.ss_name,
//         ret.dist_name,
//         ret.state,
//         ret.district,
//         ...monthCells,
//       ];
//     });



//       setReport({ rows: formattedRows, keys: filteredKeys });
//     } catch (err) {
//       console.error("Error fetching report:", err);
//       setError("Failed to load activation report.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Fetch on load / filter change
//   useEffect(() => {
//     if (companyID) fetchReport();
//   }, [companyID, fromDate, toDate, search]);

//   const totalPages = Math.ceil((report.rows || []).length / rowsPerPage);
//   const paginate = (arr, p) => arr.slice((p - 1) * rowsPerPage, p * rowsPerPage);

//   return (
//     <div className="space-y-4">
//       <GoBackButton/>
//       <Header brand={BRAND} title="Activation Report" />

//       {/* 🔹 Filters */}
//       <div className="flex flex-wrap justify-end gap-3 items-end">
//         {/* Search */}
//         <div>
//           <input
//             type="text"
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             placeholder="Search by code or name"
//             className="border rounded px-2 py-1 w-60 focus:outline-none focus:ring-2 focus:ring-blue-400"
//           />
//         </div>

//         {/* Date Range */}
//         <div className="flex items-center gap-2 justify-center">
//           <label className="block text-sm font-medium mb-1">Date Range</label>
//           <div className="border rounded px-2 py-1 focus-within:ring-2 focus-within:ring-blue-400">
//             <DatePicker
//               selectsRange
//               startDate={fromDate ? new Date(fromDate) : null}
//               endDate={toDate ? new Date(toDate) : null}
//               onChange={(dates) => {
//                 const [start, end] = dates;
//                 setFromDate(start ? start.toISOString().split("T")[0] : "");
//                 setToDate(end ? end.toISOString().split("T")[0] : "");
//               }}
//               dateFormat="yyyy-MM-dd"
//               placeholderText="Select date range"
//               className="w-full outline-none text-sm text-gray-700"
//             />
//           </div>
//         </div>

//         {/* Clear Filters */}
//           <BlueButton brand={BRAND}
//             onClick={() => {
//               setFromDate("");
//               setToDate("");
//               setSearch("");
//             }}
//             className="px-3 py-1 border rounded bg-gray-100 hover:bg-gray-200 transition-colors text-sm"
//           >
//             Reset
//           </BlueButton>

//           <BlueButton brand={BRAND} onClick={() => {fetchReport()}} className="inline-flex items-center gap-1">
//             <RefreshCw size={14} /> Refresh
//           </BlueButton>
   
//       </div>


//       {/* 🔹 Table / States */}
//       {loading && <TableSkeleton rows={10} cols={7} />}
//       {error && <p className="text-red-500">{error}</p>}

//       {!loading && !error && report.rows?.length > 0 && (
//         <>
//           <SimpleTable
//             headers={[
//               "Sr No",
//               "Retailer",
//               "CNF",
//               "Super Stockist",
//               "Distributor",
//               "State",
//               "District",
//               ...(fromDate && toDate ? report.keys : monthsTillNow),
//             ]}
//             rows={paginate(report.rows, currentPage)}
//           />

//           {/* Pagination */}
//           <div className="flex gap-2 justify-end mt-4 items-center">
//           {/* Prev Button */}
//           <button
//             disabled={currentPage === 1}
//             onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
//             className={`px-2 py-1 border rounded text-sm ${
//               currentPage === 1
//                 ? "bg-gray-200 text-gray-400 cursor-not-allowed"
//                 : "bg-white hover:bg-slate-100"
//             }`}
//           >
//             Prev
//           </button>

//           {/* Page numbers */}
//           {(() => {
//             const pages = [];
//             const maxVisible = 5; // show up to 5 pages
//             let start = Math.max(1, currentPage - 2);
//             let end = Math.min(totalPages, start + maxVisible - 1);

//             if (end - start < maxVisible - 1) {
//               start = Math.max(1, end - maxVisible + 1);
//             }

//             if (start > 1) {
//               pages.push(1);
//               if (start > 2) pages.push("...");
//             }

//             for (let i = start; i <= end; i++) {
//               pages.push(i);
//             }

//             if (end < totalPages) {
//               if (end < totalPages - 1) pages.push("...");
//               pages.push(totalPages);
//             }

//             return pages.map((p, idx) =>
//               p === "..." ? (
//                 <span key={idx} className="px-2 text-gray-500">
//                   ...
//                 </span>
//               ) : (
//                 <button
//                   key={p}
//                   onClick={() => setCurrentPage(p)}
//                   className={`px-2 py-1 border rounded text-sm ${
//                     currentPage === p
//                       ? "bg-blue-600 text-white"
//                       : "bg-white hover:bg-slate-100"
//                   }`}
//                 >
//                   {p}
//                 </button>
//               )
//             );
//           })()}

//           {/* Next Button */}
//           <button
//             disabled={currentPage === totalPages}
//             onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
//             className={`px-2 py-1 border rounded text-sm ${
//               currentPage === totalPages
//                 ? "bg-gray-200 text-gray-400 cursor-not-allowed"
//                 : "bg-white hover:bg-slate-100"
//             }`}
//           >
//             Next
//           </button>
//         </div>
//         </>
//       )}

//       {!loading && !error && report.rows?.length === 0 && <p>No data found.</p>}
//     </div>
//   );
// }




// import { useState, useEffect } from "react";
// import axios from "axios";
// import Header from "../Components/Header";
// import SimpleTable from "../Components/SimpleTable";
// import { BRAND } from "../css/brand";

// export default function ActivationReport() {
//   const [report, setReport] = useState({ rows: [], keys: [] });
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const [page, setPage] = useState(1);
//   const rowsPerPage = 10;

//   // Date range states
//   const [fromDate, setFromDate] = useState("");
//   const [toDate, setToDate] = useState("");

//   // Auth data
//   const authUserString = sessionStorage.getItem("authUserData1");
//   const authUser = JSON.parse(authUserString);
//   const companyID = authUser?.data?.[0]?.company_id;

//   // Current months till now (Jan → current)
//   const currentMonthIndex = new Date().getMonth();
//   const allMonths = [
//     "Jan", "Feb", "Mar", "Apr", "May", "Jun",
//     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
//   ];
//   const monthsTillNow = allMonths.slice(0, currentMonthIndex + 1);

//   // 🔹 Fetch Report (Year-wise or Date-wise)
//   const fetchReport = async () => {
//     setLoading(true);
//     setError(null);

//     try {
//       let res;

//       if (fromDate && toDate) {
//         // ✅ Datewise API
//         res = await axios.get(
//           `https://spillas.com/api/v1/get-datewise-activations?from_date=${fromDate}&to_date=${toDate}&company_id=${companyID}`
//         );
//       } else {
//         // ✅ Yearwise API (2025)
//         res = await axios.get(
//           `https://spillas.com/api/v1/get-yearewise-activations?year=2025&company_id=${companyID}`
//         );
//       }

//       const data =
//         res.data?.monthly_activations || res.data?.datewise_activations || {};

//       // 🔹 Map retailer data
//       const retailerMap = new Map();

//       Object.entries(data).forEach(([period, entries]) => {
//         entries.forEach((item) => {
//           const key = item.org_id;
//           if (!retailerMap.has(key)) {
//             retailerMap.set(key, {
//               retailer_name: item.retailer_name
//                 ? `RTL${item.org_id} - ${item.retailer_name}`
//                 : "-",
//               cnf_name: item.cnf_name === "null" ? "-" : item.cnf_name,
//               ss_name: item.ss_name === "null" ? "-" : item.ss_name,
//               dist_name: item.dist_name || "-",
//               state: item.state || "-",
//               district: item.district || "-",
//               totals: {},
//             });
//           }
//           retailerMap.get(key).totals[period] = item.total;
//         });
//       });

//       // 🔹 Determine which keys (months or dates) to display
//       const allKeys = Object.keys(data);
//       const filteredKeys =
//         fromDate && toDate
//           ? allKeys // show all selected date keys
//           : monthsTillNow.filter((m) => allKeys.includes(m)); // show only till current month

//       // 🔹 Format table rows
//       const formattedRows = Array.from(retailerMap.values()).map(
//         (ret, index) => [
//           index + 1,
//           <span className="text-blue-800 font-semibold">
//             {ret.retailer_name}
//           </span>,
//           ret.cnf_name,
//           ret.ss_name,
//           ret.dist_name,
//           ret.state,
//           ret.district,
//           ...filteredKeys.map((k) => ret.totals[k] || 0),
//         ]
//       );

//       setReport({ rows: formattedRows, keys: filteredKeys });
//     } catch (err) {
//       console.error("Error fetching report:", err);
//       setError("Failed to load activation report.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // 🔹 Fetch on load or when filters change
//   useEffect(() => {
//     if (companyID) fetchReport();
//   }, [companyID, fromDate, toDate]);

//   const totalPages = Math.ceil((report.rows || []).length / rowsPerPage);
//   const paginate = (arr, p) => arr.slice((p - 1) * rowsPerPage, p * rowsPerPage);

//   return (
//     <div className="space-y-4">
//       <Header brand={BRAND} title="Activation Report" />

//       {/* 🔹 Date Range Filters */}
//       <div className="flex flex-wrap gap-3 items-end">
//         <div>
//           <label className="block text-sm font-medium mb-1">From Date</label>
//           <input
//             type="date"
//             value={fromDate}
//             onChange={(e) => setFromDate(e.target.value)}
//             className="border rounded px-2 py-1"
//           />
//         </div>
//         <div>
//           <label className="block text-sm font-medium mb-1">To Date</label>
//           <input
//             type="date"
//             value={toDate}
//             onChange={(e) => setToDate(e.target.value)}
//             className="border rounded px-2 py-1"
//           />
//         </div>
//         {(fromDate || toDate) && (
//           <button
//             onClick={() => {
//               setFromDate("");
//               setToDate("");
//             }}
//             className="px-3 py-1 border rounded bg-gray-100 hover:bg-gray-200"
//           >
//             Clear
//           </button>
//         )}
//       </div>

//       {/* 🔹 Data Display */}
//       {loading && <p>Loading...</p>}
//       {error && <p className="text-red-500">{error}</p>}

//       {!loading && !error && report.rows?.length > 0 && (
//         <>
//           <SimpleTable
//             headers={[
//               "Sr No",
//               "Retailer",
//               "CNF",
//               "Super Stockist",
//               "Distributor",
//               "State",
//               "District",
//               ...(fromDate && toDate ? report.keys : monthsTillNow),
//             ]}
//             rows={paginate(report.rows, page)}
//           />

//           {/* Pagination */}
//           <div className="flex justify-center gap-2 mt-3">
//             <button
//               disabled={page === 1}
//               onClick={() => setPage(page - 1)}
//               className="px-3 py-1 border rounded disabled:opacity-50"
//             >
//               Prev
//             </button>
//             <span>
//               {page} / {totalPages}
//             </span>
//             <button
//               disabled={page === totalPages}
//               onClick={() => setPage(page + 1)}
//               className="px-3 py-1 border rounded disabled:opacity-50"
//             >
//               Next
//             </button>
//           </div>
//         </>
//       )}

//       {!loading && !error && report.rows?.length === 0 && (
//         <p>No data found.</p>
//       )}
//     </div>
//   );
// }



// import { useState, useEffect } from "react";
// import axios from "axios";
// import Header from "../Components/Header";
// import SimpleTable from "../Components/SimpleTable";
// import { BRAND } from "../css/brand";

// export default function ActivationReport() {
//   const [report, setReport] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const [page, setPage] = useState(1);
//   const rowsPerPage = 10;

//   const authUserString = sessionStorage.getItem("authUserData1");
//   const authUser = JSON.parse(authUserString);
//   const companyID = authUser?.data[0]?.company_id;

//   const currentMonthIndex = new Date().getMonth(); // 0-based
//   const months = [
//     "Jan", "Feb", "Mar", "Apr", "May", "Jun",
//     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
//   ].slice(0, currentMonthIndex + 1); // show till current month only

//   useEffect(() => {
//     const fetchReport = async () => {
//       setLoading(true);
//       setError(null);

//       try {
//         const res = await axios.get(
//           `https://spillas.com/api/v1/get-yearewise-activations?year=2025&company_id=${companyID}`
//         );

//         const monthlyData = res.data?.monthly_activations || {};

//         // Collect unique retailers across months
//         const retailerMap = new Map();

//         Object.entries(monthlyData).forEach(([month, entries]) => {
//           entries.forEach((item) => {
//             const key = item.org_id;
//             if (!retailerMap.has(key)) {
//               retailerMap.set(key, {
//                 retailer_name: item.retailer_name || "-",
//                 cnf_name: item.cnf_name === "null" ? "-" : item.cnf_name,
//                 ss_name: item.ss_name === "null" ? "-" : item.ss_name,
//                 dist_name: item.dist_name || "-",
//                 state: item.state || "-",
//                 district: item.district || "-",
//                 monthly_totals: {},
//               });
//             }
//             retailerMap.get(key).monthly_totals[month] = item.total;
//           });
//         });

//         const formattedRows = Array.from(retailerMap.values()).map((ret, index) => [
//           index + 1,
//           ret.retailer_name,
//           ret.cnf_name,
//           ret.ss_name,
//           ret.dist_name,
//           ret.state,
//           ret.district,
//           ...months.map((m) => ret.monthly_totals[m] || 0),
//         ]);

//         setReport(formattedRows);
//       } catch (err) {
//         console.error("Error fetching report:", err);
//         setError("Failed to load activation report.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (companyID) fetchReport();
//   }, [companyID]);

//   const totalPages = Math.ceil(report.length / rowsPerPage);
//   const paginate = (arr, p) => arr.slice((p - 1) * rowsPerPage, p * rowsPerPage);

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header brand={BRAND} title="Activation Report" />

//       {loading && <p>Loading...</p>}
//       {error && <p className="text-red-500">{error}</p>}

//       {!loading && !error && report.length > 0 && (
//         <>
//           <SimpleTable
//             headers={[
//               "Sr No",
//               "Retailer",
//               "CNF",
//               "SS",
//               "DIST",
//               "State",
//               "District",
//               ...months,
//             ]}
//             rows={paginate(report, page)}
//           />

//           {/* Pagination */}
//           <div className="flex justify-center gap-2 mt-2">
//             <button
//               disabled={page === 1}
//               onClick={() => setPage(page - 1)}
//               className="px-3 py-1 border rounded disabled:opacity-50"
//             >
//               Prev
//             </button>
//             <span>
//               {page} / {totalPages}
//             </span>
//             <button
//               disabled={page === totalPages}
//               onClick={() => setPage(page + 1)}
//               className="px-3 py-1 border rounded disabled:opacity-50"
//             >
//               Next
//             </button>
//           </div>
//         </>
//       )}

//       {!loading && !error && report.length === 0 && <p>No data found</p>}
//     </div>
//   );
// }
